/**
 * InheritanceManager.ts - Advanced style inheritance system
 * 
 * CHANGE HISTORY:
 * - 251102_1653 danielbeer: Created for Phase 3.5a - Inheritance & Variants
 * 
 * Provides:
 * - Multi-level inheritance resolution
 * - Inheritance chain tracking
 * - Circular dependency detection
 * - Inheritance tree visualization
 * - Variable override tracking
 */

import type { Style } from '../types';

export interface InheritanceChain {
  styleId: string;
  depth: number;
  parent: InheritanceChain | null;
}

export interface InheritanceTree {
  root: Style;
  children: InheritanceTree[];
  depth: number;
}

export interface VariableOverride {
  variable: string;
  originalValue: string;
  overriddenValue: string;
  overriddenBy: string; // Style ID
  depth: number;
}

export interface InheritanceAnalysis {
  chain: InheritanceChain;
  totalDepth: number;
  overrides: VariableOverride[];
  circularDependency: boolean;
  missingParents: string[];
}

/**
 * InheritanceManager - Manages style inheritance
 */
export class InheritanceManager {
  private styles: Map<string, Style>;

  constructor(styles: Map<string, Style>) {
    this.styles = styles;
  }

  /**
   * Get inheritance chain for a style
   */
  getInheritanceChain(styleId: string): InheritanceChain | null {
    const visited = new Set<string>();
    return this.buildChain(styleId, 0, visited);
  }

  /**
   * Build inheritance chain recursively
   */
  private buildChain(
    styleId: string,
    depth: number,
    visited: Set<string>
  ): InheritanceChain | null {
    const style = this.styles.get(styleId);
    if (!style) return null;

    // Circular dependency check
    if (visited.has(styleId)) {
      console.warn(`Circular dependency detected in style inheritance: ${styleId}`);
      return null;
    }

    visited.add(styleId);

    const chain: InheritanceChain = {
      styleId,
      depth,
      parent: null
    };

    if (style.extends) {
      chain.parent = this.buildChain(style.extends, depth + 1, visited);
    }

    return chain;
  }

  /**
   * Get inheritance tree (all descendants)
   */
  getInheritanceTree(rootStyleId: string): InheritanceTree | null {
    const root = this.styles.get(rootStyleId);
    if (!root) return null;

    return this.buildTree(root, 0);
  }

  /**
   * Build inheritance tree recursively
   */
  private buildTree(style: Style, depth: number): InheritanceTree {
    const children: InheritanceTree[] = [];

    // Find all styles that extend this one
    this.styles.forEach((childStyle) => {
      if (childStyle.extends === style.id) {
        children.push(this.buildTree(childStyle, depth + 1));
      }
    });

    return {
      root: style,
      children,
      depth
    };
  }

  /**
   * Analyze inheritance for a style
   */
  analyzeInheritance(styleId: string): InheritanceAnalysis {
    const chain = this.getInheritanceChain(styleId);
    const overrides: VariableOverride[] = [];
    const missingParents: string[] = [];
    let circularDependency = false;

    if (!chain) {
      return {
        chain: { styleId, depth: 0, parent: null },
        totalDepth: 0,
        overrides: [],
        circularDependency: false,
        missingParents: []
      };
    }

    // Calculate total depth
    let totalDepth = 0;
    let current: InheritanceChain | null = chain;
    while (current) {
      totalDepth++;
      current = current.parent;
    }

    // Track variable overrides
    const variableOrigins = new Map<string, { value: string; styleId: string; depth: number }>();
    
    // Walk chain from root to leaf
    const chainArray: InheritanceChain[] = [];
    current = chain;
    while (current) {
      chainArray.unshift(current);
      current = current.parent;
    }

    chainArray.forEach((link, index) => {
      const style = this.styles.get(link.styleId);
      if (!style) {
        missingParents.push(link.styleId);
        return;
      }

      Object.entries(style.variables).forEach(([key, value]) => {
        const origin = variableOrigins.get(key);
        if (origin) {
          // Variable is being overridden
          overrides.push({
            variable: key,
            originalValue: origin.value,
            overriddenValue: String(value),
            overriddenBy: style.id,
            depth: index
          });
        }
        
        // Update origin
        variableOrigins.set(key, {
          value: String(value),
          styleId: style.id,
          depth: index
        });
      });
    });

    return {
      chain,
      totalDepth,
      overrides,
      circularDependency,
      missingParents
    };
  }

  /**
   * Get all ancestors of a style
   */
  getAncestors(styleId: string): Style[] {
    const ancestors: Style[] = [];
    const chain = this.getInheritanceChain(styleId);
    
    let current = chain?.parent;
    while (current) {
      const style = this.styles.get(current.styleId);
      if (style) {
        ancestors.push(style);
      }
      current = current.parent;
    }

    return ancestors;
  }

  /**
   * Get all descendants of a style
   */
  getDescendants(styleId: string): Style[] {
    const descendants: Style[] = [];
    const tree = this.getInheritanceTree(styleId);
    
    if (tree) {
      this.collectDescendants(tree, descendants);
    }

    return descendants;
  }

  /**
   * Collect descendants recursively
   */
  private collectDescendants(tree: InheritanceTree, result: Style[]): void {
    tree.children.forEach(child => {
      result.push(child.root);
      this.collectDescendants(child, result);
    });
  }

  /**
   * Check if style has circular dependency
   */
  hasCircularDependency(styleId: string): boolean {
    const visited = new Set<string>();
    let current = styleId;

    while (current) {
      if (visited.has(current)) {
        return true;
      }
      visited.add(current);

      const style = this.styles.get(current);
      if (!style || !style.extends) {
        break;
      }
      current = style.extends;
    }

    return false;
  }

  /**
   * Get inheritance depth
   */
  getDepth(styleId: string): number {
    const chain = this.getInheritanceChain(styleId);
    let depth = 0;
    let current = chain;
    
    while (current) {
      depth++;
      current = current.parent;
    }

    return depth;
  }

  /**
   * Visualize inheritance chain as string
   */
  visualizeChain(styleId: string): string {
    const chain = this.getInheritanceChain(styleId);
    if (!chain) return styleId;

    const parts: string[] = [];
    let current: InheritanceChain | null = chain;

    while (current) {
      const style = this.styles.get(current.styleId);
      parts.push(style?.name || current.styleId);
      current = current.parent;
    }

    return parts.join(' → ');
  }

  /**
   * Visualize inheritance tree as string
   */
  visualizeTree(styleId: string, indent = 0): string {
    const tree = this.getInheritanceTree(styleId);
    if (!tree) return '';

    const lines: string[] = [];
    const prefix = '  '.repeat(indent);
    
    lines.push(`${prefix}${tree.root.name} (${tree.root.id})`);
    
    tree.children.forEach(child => {
      lines.push(this.visualizeTree(child.root.id, indent + 1));
    });

    return lines.join('\n');
  }
}

/**
 * Singleton instance
 */
let inheritanceManagerInstance: InheritanceManager | null = null;

export function getInheritanceManager(styles: Map<string, Style>): InheritanceManager {
  if (!inheritanceManagerInstance || inheritanceManagerInstance['styles'] !== styles) {
    inheritanceManagerInstance = new InheritanceManager(styles);
  }
  return inheritanceManagerInstance;
}

export function resetInheritanceManager(): void {
  inheritanceManagerInstance = null;
}
